// Phase M+3.7 — module-level cache of Role.displayLabel{En,Fr,Nl} keyed by
// Role.name. Loaded once per session from /api/roles/labels and consulted
// by the roleLabel(name) helper at every render.
//
// Why a separate hook from useTaxonomy: roles live in a dedicated table
// (not TaxonomyEntry) because they carry permission-defaults JSON, ratio,
// system flags. The labels endpoint returns just the localised names so
// every component can render a user-language role badge without pulling
// the full role record.

import { useEffect, useState } from "react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import type { Role } from "@shared/entities";

const state: {
  rows: Role[];
  loading: boolean;
  fetched: boolean;
  inflight: Promise<void> | null;
} = { rows: [], loading: false, fetched: false, inflight: null };
const subs = new Set<() => void>();
function notify() {
  for (const cb of subs) cb();
}

async function load() {
  if (state.inflight) return state.inflight;
  state.loading = true;
  state.inflight = (async () => {
    try {
      const rows = await api.get("/roles/labels");
      state.rows = Array.isArray(rows) ? rows : [];
      state.fetched = true;
    } catch {
      state.rows = [];
    } finally {
      state.loading = false;
      state.inflight = null;
      notify();
    }
  })();
  return state.inflight;
}

export function useRoleLabels() {
  const [, setTick] = useState(0);
  const { lang } = useTranslation();
  useEffect(() => {
    const rerender = () => setTick((t) => t + 1);
    subs.add(rerender);
    if (!state.fetched && !state.inflight) load();
    return () => {
      subs.delete(rerender);
    };
  }, []);
  return { rows: state.rows, lang };
}

// Imperative resolver: pick the active-language displayLabel for a role.name.
// Falls back displayLabelXX → displayLabelEn → displayLabel → name.
export function roleLabel(name: string) {
  if (!name) return "";
  const lang = (typeof window !== "undefined" && window.__lxLang) || "en";
  const r = state.rows.find((x) => x.name === name);
  if (!r) return name;
  if (lang === "fr" && r.displayLabelFr) return r.displayLabelFr;
  if (lang === "nl" && r.displayLabelNl) return r.displayLabelNl;
  return r.displayLabelEn || r.displayLabel || r.name;
}

export function primeRoleLabels() {
  return load();
}

// Phase auth-c2 Step 6 — imperative resolver for the per-role default
// billing ratio. Replaces the hardcoded ROLE_RATIO map. Returns the
// integer percent set on the Role row, or null if the role is unknown
// or the labels list hasn't loaded yet. Consumers should chain with
// `?? 100` to retain the final-default behaviour.
export function roleDefaultRatio(name: string) {
  if (!name) return null;
  const r = state.rows.find((x) => x.name === name);
  if (!r) return null;
  return Number.isFinite(r.defaultRatio) ? r.defaultRatio : null;
}
